import React, { FC, MutableRefObject, ReactElement, useContext, useEffect, useState } from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import { SelfProvisioning as SelfProvisioningLanguage } from '../../../../i18n'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import {
    CMEResponse,
    PartitionAdminResponse,
    SessionAPIResponse,
    TelSysAdminResponse
} from '../../../NonReact/Types/Types'
import { getSession } from '../../Common/API/CommonAPI'
import { LanguageContext, MessageContext, UserInfoContext } from '../../Common/Contexts'
import { MessageContextType } from '../../Common/Contexts/MessageContext'
import { ModalContext, ModalContextType } from '../../Common/Contexts/ModalContext'
import { UserInfoContextType } from '../../Common/Contexts/UserInfoContext'
import { ReactState } from '../../Common/Types/Types'
import { encrypt } from '../../Common/Utils/Security'
import { CommsStatuses, EntityCreateStatuses, FAILED_STATES, SelfProvisioningViews } from '../Constants/Constants'
import { SelfProvisioningConfig, SelfProvisioningConfigContext } from '../Contexts/SelfProvisioningConfigContext'
import { SelfProvisioningContext, SelfProvisioningContextType } from '../Contexts/SelfProvisioningContext'
import { CompletedView } from '../Views/CompletedView'
import { InitialDataEntry } from '../Views/InitialDataEntry'
import { ProvisioningWaitScreen } from '../Views/ProvisioningWaitScreen'
import { ReviewView } from '../Views/ReviewView'
import { UserEntry } from '../Views/UserEntry'
import { Welcome } from '../Views/Welcome'
import {
    createPartition,
    createTelSys,
    getCME,
    getPartition,
    getTelSys
} from '../../../NonReact/common/API/EntityAPIClient'
import { JSONResponse } from '../../../NonReact/common/Types/Types'

/**
 * The Self Provisioning Wizard container, this controls the flow of the wizard.
 */
export const SelfProvisioning: FC = (): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    const {
        api: { maxCommStatusChecks, commsInactiveGraceChecks, heartbeatPeriodMillisecs },
        telServerDefaults: { commsEnabled, ipAddrHost1, telSysType, xsiActions, xsiEvents, billingTag },
        partitionDefaults: {
            commsEnabled: partitionCommsEnabled,
            licenceLevel,
            syncNow,
            syncOptions,
            syncType,
            telSysComponentType,
            billedMonthly
        }
    }: SelfProvisioningConfig = useContext(SelfProvisioningConfigContext)
    const {
        cmeState: [, setCMEName],
        deleteableTelSysState: [, setDeleteableTelSys],
        deleteablePartitionState: [, setDeleteablePartition],
        telSysState: [, setTelSysID],
        telSysStatus: [, setTelSysStatus],
        partitionState: [, setPartitionID],
        partitionStatus: [, setPartitionStatus],
        formState: [formState],
        viewState: [view],
        previousStep
    }: SelfProvisioningContextType = useContext(SelfProvisioningContext)

    const currentUser: UserInfoContextType = useContext(UserInfoContext)
    const { displayError }: MessageContextType = useContext(MessageContext)
    const { setModalTitle }: ModalContextType = useContext(ModalContext)
    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const [sessionNonce, setSessionNonce]: ReactState<Definable<string>> = useState<Definable<string>>()

    /**
     * Handle a REST API error.
     *
     * @param e - Error object.
     */
    const handleError = (e: Error): void => {
        if (isMounted.current) {
            displayError(e)
            previousStep()
        }
    }

    useEffect((): void => {
        if (setModalTitle) {
            // If we are on the provisioning wait screen, set the modal title to the provisioning wait screen title.
            if (view === 2) {
                setModalTitle(
                    language.getInterpolated(SelfProvisioningLanguage.SelfProvisioning_Setup_Info, formState?.siteName)
                )
            } else {
                setModalTitle(language.get(SelfProvisioningLanguage.SelfProvisioning_ModalTitle))
            }
        }
    }, [view])

    /**
     * Function to run on partition creation.
     *
     * @param telSys - Tel Server object.
     * @param partition - Partition object.
     */
    const onSuccessfulPartitionCreate = (
        telSys: TelSysAdminResponse,
        partition: Nullable<PartitionAdminResponse>
    ): void => {
        if (isMounted.current) {
            if (partition?.PartIDInTelSys) {
                setDeleteablePartition(partition?.PartIDInTelSys)
                setPartitionID(partition?.PartIDInTelSys)
                setPartitionStatus(EntityCreateStatuses.CREATED)

                // If the partition was created successfully, then we need to check through a hearthbeat on the status until it is either
                // successful or failed.
                const checkSyncStatusPartition = (
                    numberOfChecksPartition: number,
                    commsInactiveGraceCountPartition: number
                ): void => {
                    if (telSys?.TelSysID && partition?.PartIDInTelSys) {
                        getPartition(telSys.TelSysID, partition?.PartIDInTelSys)
                            .then((partitionResponse: JSONResponse<PartitionAdminResponse>): void => {
                                if (isMounted.current) {
                                    // If the partition is in a successful state, then set state to synced.
                                    if (
                                        partitionResponse?.CommsStatus === CommsStatuses.SYNCHRONISED ||
                                        partitionResponse?.CommsStatus === CommsStatuses.RUNNING ||
                                        partitionResponse?.CommsStatus === CommsStatuses.RUNNING_WARNINGS
                                    ) {
                                        setPartitionStatus(EntityCreateStatuses.SYNCED)
                                    } else if (
                                        partitionResponse?.CommsStatus &&
                                        numberOfChecksPartition <= maxCommStatusChecks &&
                                        !FAILED_STATES.includes(partitionResponse?.CommsStatus) &&
                                        (partitionResponse?.CommsStatus !== CommsStatuses.COMMS_INACTIVE ||
                                            (partitionResponse?.CommsStatus === CommsStatuses.COMMS_INACTIVE &&
                                                commsInactiveGraceCountPartition <= commsInactiveGraceChecks)) &&
                                        (partitionResponse?.CommsStatus !== CommsStatuses.SYNCHRONISATION_PENDING ||
                                            (partitionResponse?.CommsStatus === CommsStatuses.SYNCHRONISATION_PENDING &&
                                                commsInactiveGraceCountPartition <= commsInactiveGraceChecks))
                                    ) {
                                        // Partition is in an indeterminate state.
                                        setTimeout(
                                            (): void =>
                                                checkSyncStatusPartition(
                                                    numberOfChecksPartition + 1,
                                                    partitionResponse?.CommsStatus === CommsStatuses.COMMS_INACTIVE ||
                                                        partitionResponse?.CommsStatus ===
                                                            CommsStatuses.SYNCHRONISATION_PENDING
                                                        ? commsInactiveGraceCountPartition + 1
                                                        : commsInactiveGraceCountPartition
                                                ),
                                            heartbeatPeriodMillisecs
                                        )
                                    } else {
                                        // Partition synced has failed.
                                        setPartitionStatus(EntityCreateStatuses.FAILED)
                                    }
                                }
                            })
                            .catch((): void => {
                                if (isMounted.current) {
                                    setPartitionStatus(EntityCreateStatuses.FAILED)
                                }
                            })
                    }
                }

                checkSyncStatusPartition(0, 0)
            } else {
                previousStep()
            }
        }
    }

    /**
     * Function to run on tel server creation.
     *
     * @param telSys - Tel Server object.
     */
    const onSuccessfulTelSysCreate = (telSys: JSONResponse<TelSysAdminResponse>): void => {
        if (telSys?.TelSysID && isMounted.current) {
            setDeleteableTelSys(telSys?.TelSysID)
            setTelSysID(telSys?.TelSysID)
            setTelSysStatus(EntityCreateStatuses.CREATED)

            let numberOfChecks: number = 0
            let commsInactiveGraceCount: number = 0

            // If the telephony server was created successfully, then we need to check through a hearthbeat on the status until it is either
            // successful or failed.
            const checkSyncStatus = (): void => {
                if (telSys?.TelSysID) {
                    numberOfChecks += 1
                    // Get the tel server information, CommStatus is required.
                    getTelSys(telSys.TelSysID)
                        .then((telSysResponse: JSONResponse<TelSysAdminResponse>): void => {
                            if (isMounted.current) {
                                if (
                                    telSysResponse?.CommsStatus === CommsStatuses.RUNNING ||
                                    telSysResponse?.CommsStatus === CommsStatuses.RUNNING_WARNINGS
                                ) {
                                    // Succcessful state.
                                    setTelSysStatus(EntityCreateStatuses.SYNCED)
                                } else if (
                                    telSysResponse?.CommsStatus &&
                                    numberOfChecks <= maxCommStatusChecks &&
                                    !FAILED_STATES.includes(telSysResponse?.CommsStatus) &&
                                    (telSysResponse?.CommsStatus !== CommsStatuses.COMMS_INACTIVE ||
                                        (telSysResponse?.CommsStatus === CommsStatuses.COMMS_INACTIVE &&
                                            commsInactiveGraceCount <= commsInactiveGraceChecks))
                                ) {
                                    // Indeterminate state.
                                    setTimeout(checkSyncStatus, heartbeatPeriodMillisecs)
                                } else {
                                    // Failed state.
                                    setTelSysStatus(EntityCreateStatuses.FAILED)
                                }

                                if (telSysResponse?.CommsStatus === CommsStatuses.COMMS_INACTIVE) {
                                    commsInactiveGraceCount += 1
                                }
                            }
                        })
                        .catch((): void => {
                            if (isMounted.current) {
                                setTelSysStatus(EntityCreateStatuses.FAILED)
                            }
                        })
                }
            }

            checkSyncStatus()

            // Create the partition.
            createPartition(telSys.TelSysID, {
                Description: formState?.siteName ?? '',
                CommsEnabled: partitionCommsEnabled,
                LicensedForRptUserType: licenceLevel,
                SyncNow: syncNow,
                SyncType: syncType,
                IDOnTelSysOwner: formState?.enterpriseID ?? '',
                TelSysComponentType: telSysComponentType,
                SyncOptions: syncOptions,
                BilledMonthly: billedMonthly
            })
                .then((partition: JSONResponse<PartitionAdminResponse>): void =>
                    onSuccessfulPartitionCreate(telSys, partition)
                )
                .catch(handleError)
        } else if (isMounted.current) {
            previousStep()
        }
    }

    useEffect((): void => {
        if (formState && currentUser) {
            // Get the session as the Nonce is required for encrypting the password.
            getSession()
                .then((session: JSONResponse<SessionAPIResponse>): void => {
                    if (!session) {
                        // If no session is found throw an error.
                        throw new Error(language.get(SelfProvisioningLanguage.SelfProvisioning_Errors_SessionNotFound))
                    }

                    setSessionNonce(session?.Nonce)

                    // Get the CME so we can add the description to the new tel server name.
                    getCME(currentUser.Scope.CMEnvironmentID)
                        .then((cme: Nullable<CMEResponse>): void => {
                            if (cme) {
                                setCMEName(cme.Description)
                                // Create the new tel server.
                                createTelSys({
                                    Description: `${cme.Description}:${formState?.siteName}` ?? '',
                                    CommsEnabled: commsEnabled,
                                    IPAddrOrHost1: ipAddrHost1,
                                    IPAddrOrHost3: ipAddrHost1,
                                    AuthUserID2: formState?.userName,
                                    AuthPassword2: encrypt(formState?.password, session?.Nonce),
                                    AuthUserID4: formState?.userName,
                                    AuthPassword4: encrypt(formState?.password, session?.Nonce),
                                    TelSysType: telSysType,
                                    CMEnvironmentID: currentUser?.Scope?.CMEnvironmentID ?? '',
                                    CommsParameter1: xsiEvents,
                                    CommsParameter2: xsiActions,
                                    BillingTag: billingTag
                                })
                                    .then(onSuccessfulTelSysCreate)
                                    .catch(handleError)
                            }
                        })
                        .catch(handleError)
                })
                .catch(handleError)
        }
    }, [formState])

    // Determine which view to show.
    const viewSwitch = (): ReactElement => {
        switch (view) {
            case SelfProvisioningViews.WELCOME:
                return <Welcome />
            case SelfProvisioningViews.INITIAL_DATA_ENTRY:
                return <InitialDataEntry />
            case SelfProvisioningViews.WAIT_SCREEN:
                return <ProvisioningWaitScreen />
            case SelfProvisioningViews.CREATE_USERS:
                return <UserEntry sessionNonce={sessionNonce} />
            case SelfProvisioningViews.REVIEW:
                return <ReviewView />
            case SelfProvisioningViews.COMPLETE:
                return <CompletedView />
            default:
                return <></>
        }
    }

    return <div>{viewSwitch()}</div>
}
